/* The blocks inside a page — whichever kind of page it is.
 *
 * A website page and a funnel page-step hold the same thing: an ordered list of
 * typed copy blocks. `funnel_sections` is column-for-column `website_sections`
 * with a different parent key (see supabase/0001_momentum_schema.sql), so the
 * store over it is the same store. This is that store, with the table and the
 * parent column passed in.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * Seeding blocks out of locked Accelerator prose, and the opt-in resync after
 * she re-locks — only website pages come FROM anything, so that half stays in
 * websiteSections.js, built on the `save` / `remove` / `newId` returned below.
 * A funnel page is written from empty every time.
 */

import { useCallback, useMemo } from "react";
import { createRecordStore } from "./recordStore";
import { reorderWrites } from "./reorder";
import { OTHER_KIND, emptyContent, kindDef, sectionWords } from "./websiteSectionKinds";

/**
 * The camelCase fields a sections table owns, given the name of its parent key.
 *
 * One list for both tables because they really are the same columns. The four
 * `source*` fields stay null on a funnel page today; they are what a seed from
 * locked copy would fill, and leaving them out would mean discovering the gap
 * later as a column that silently never writes.
 */
export const sectionFields = (parentField) => [
  parentField, "sectionType", "sortOrder", "content", "status",
  "source", "sourceSection", "sourceText", "sourceHash",
  /* Per-field word budgets that override the kind defaults, added by
     supabase/0017. Sparse — only fields she has changed — and deliberately NOT
     inside `content`, which is the copy Brill's drafts get merged into. */
  "limits",
];

/**
 * Every word on a page, across every field of every block.
 *
 * Counted from the RAW stored values, never from a heading fallback: falling
 * back to a kind label when she hasn't named a block would report words on a
 * page that is completely empty, which is precisely the state this number
 * exists to reveal.
 */
export function pageWords(sections = []) {
  return (sections || []).reduce((n, s) => n + sectionWords(s), 0);
}

/**
 * Where a page's drafting chat starts: the banner, or the top of the page when
 * there isn't one.
 *
 * The banner rather than the first row because it is the top of the finished
 * page and the block she'd write first, and because a page split out of her
 * locked prose does not reliably come back banner-first.
 *
 * Null on an empty page — the caller has nothing to open the chat onto, and the
 * empty state already tells her to add a block.
 */
export function startSection(list = []) {
  return list.find((s) => s?.sectionType === "banner") || list[0] || null;
}

/**
 * Build the hook for one sections table.
 *
 * @param table       Postgres table, e.g. "website_sections"
 * @param parentField camelCase parent key, e.g. "pageId" / "funnelStepId"
 * @param idPrefix    short prefix for generated ids, e.g. "wsc"
 */
export function createSectionStore({ table, parentField, idPrefix }) {
  const useSectionRecords = createRecordStore({
    table,
    fields: sectionFields(parentField),
    idPrefix,
    // No clerk_user_id on either table — a block belongs to its page, and the
    // RLS policy walks that key up to her. See recordStore's note on ownerColumn.
    ownerColumn: null,
    orderBy: "sort_order",
    ascending: true,
  });

  return function useSections() {
    const { rows, loading, error, save, remove, reload, clearError, newId } = useSectionRecords();

    /* Every block of every page in one query rather than one per page — the
       tables want a block count on each row, so fetching per page would be a
       request inside a render loop. */
    const byParent = useMemo(() => {
      const map = new Map();
      for (const s of rows || []) {
        const list = map.get(s[parentField]) || [];
        list.push(s);
        map.set(s[parentField], list);
      }
      // Sorted here rather than trusting the query: a row saved during this
      // session is put at the head of `rows` by the store, which is right for a
      // newest-first list and wrong for an ordered one.
      for (const list of map.values()) list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      return map;
    }, [rows]);

    const sectionsFor = useCallback((parentId) => byParent.get(parentId) || [], [byParent]);

    /** A blank block of the kind she picked, at the bottom. Hers from the start —
     *  no source, so a refresh from locked copy leaves it alone. */
    const addSection = useCallback(async (parentId, kind = OTHER_KIND) => {
      const existing = sectionsFor(parentId);
      const last = existing.length ? (existing[existing.length - 1].sortOrder ?? existing.length - 1) : -1;
      return save({
        id: newId(),
        [parentField]: parentId,
        sectionType: kindDef(kind).id,
        sortOrder: last + 1,
        content: emptyContent(kind),
        status: "draft",
        source: null,
      });
    }, [sectionsFor, save, newId]);

    /** Retype an existing block as a different kind.
     *
     *  Deliberately keeps `content` whole. The new kind renders its own fields and
     *  ignores the rest, so switching back brings her words straight back — where
     *  clearing the content would make one wrong click destroy the block. What the
     *  new kind can't show, the editor tells her about (see `hiddenFieldKeys`). */
    const changeKind = useCallback(async (section, kind) => {
      const next = kindDef(kind).id;
      if (!section?.id || next === kindDef(section.sectionType).id) return section;
      return save({ ...section, sectionType: next, content: { ...emptyContent(next), ...(section.content || {}) } });
    }, [save]);

    /** Move a block to an arbitrary position, which is what a drag produces.
     *
     *  A neighbour swap can't express "drag row 1 to the end", so this renumbers
     *  the whole page 0..n-1 in the new order — which also repairs pages whose
     *  rows share a `sort_order`. See utils/reorder.js. */
    const reorderSections = useCallback(async (parentId, from, to) => {
      const writes = reorderWrites(sectionsFor(parentId), from, to);
      let ok = true;
      // Sequential rather than parallel: recordStore.save() replaces rows in local
      // state one at a time, and racing them can interleave writes onto a stale
      // copy of the list.
      for (const row of writes) if (!(await save(row))) ok = false;
      return ok;
    }, [sectionsFor, save]);

    return {
      sections: rows, loading, error, clearError, reload,
      sectionsFor,
      saveSection: save,
      deleteSection: remove,
      addSection,
      changeKind,
      reorderSections,
      // The raw three, for a store that builds seeding on top of this one.
      save, remove, newId,
    };
  };
}
